import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson, FarmRiskType } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import useOptions from 'core/hooks/use-options';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import {
  getConstructionTypeExemptPropertyTypes,
  getDwellingConstructionTypeOptions,
  getPropertyTypeOptions,
  getMaskedResidenceUsageOptions
} from 'common/helpers/farm-helpers';
import { useClearInvalidRiskOptions } from 'common/helpers/use-clear-invalid-risk-options';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import { BarndomininumDwellingForm } from 'common/components/farm/barndominium-dwelling-form';
import { MobileHomeForm } from 'common/components/farm/mobile-home-form';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';
import {
  isBarndominium as isBarndominiumBuilding,
  isMobileHome as isMobileHomeBuilding,
  isModularHome as isModularHomeBuilding
} from 'common/components/farm/locations/risks/risk-types/property-type-helpers';

import useStyles from './initial-risk-detail.styles';

// Form options constants
const nfidOptions = lookupsJson.farm.numFamiliesInDwellingOptions;
const ownershipStatusOptions = lookupsJson.farm.ownershipStatus;
const tohOptions = lookupsJson.typeOfHome;
const hqOptions = lookupsJson.homeQuality;
const btOptions = lookupsJson.basementType;
const gtOptions = lookupsJson.garageType;
const roOptions = lookupsJson.roofOriginal;
const rtOptions = lookupsJson.farm.dwellingRoofType;
const rsOptions = lookupsJson.roofShape;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;

// `missingDataCodes`: rejection codes the caller already knows the quote has. The offer sidebar's
// 50006 fix button (resubmit-offer-modal) passes the offer's code, because that rejection is on the
// offer, not in the new-quote store's alerts, and without it the replacement-cost fields stayed
// hidden (#36621 follow-up).
const FO3RiskDetail = ({ missingDataCodes = [] }) => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const ctOptions = getDwellingConstructionTypeOptions(FarmRiskType.Fo3, values.policyType);
  const needMoreData = [...alerts.map((alert) => alert.code), ...missingDataCodes].some((code) =>
    [5004, 50006].includes(Number(code))
  );
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;

  // Resolve the residence-usage mask from the primary LOCATION's state, not the applicant/mailing
  // state — Forreston only configures IL masks, so an out-of-state mailing address finds no mask and
  // the helper falls back to the unfiltered base options. That leaks Tenant Occupied - Rental ('R')
  // onto Home (Z) and shows everything on Rental (Y) instead of R-only (#32094). Fall back to the
  // mailing state only before a location address is entered. (Matches PR #32163's risk-type fix.)
  const residenceMaskState = farmLocation?.address?.state || values.state;

  // Brand-aware so CFM/MO gets its extra Exterior Wall Finish options (Stone Veneer Natural, Log
  // Siding); every other brand falls back to the base list. Resolved from the dwelling's location
  // state (same source as the residence-usage mask above). #34910
  const ewtOptions = useOptions('exteriorWallType', residenceMaskState);

  // Occupancy options depend on BOTH the risk type and the policy-type mask, so they must recompute
  // when the operator switches policy type (Home Z ↔ Rental Y). The dropdown Field below is rendered
  // with fast={false} for that reason — a FastField only re-renders on its own value change, so a
  // change to values.policyType would otherwise leave the options stale.
  const residenceUsageOptions = getMaskedResidenceUsageOptions(farmRisk?.type, values.policyType, residenceMaskState);
  const currentResidenceUsage = farmRisk?.building?.residenceUsage;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (needMoreData) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [needMoreData]);

  // Clear invalid Structure Category and Residence Type/Usage values when risk type changes
  useClearInvalidRiskOptions({
    riskType: farmRisk?.type,
    propertyType: farmRisk?.building?.propertyType,
    residenceUsage: farmRisk?.building?.residenceUsage,
    setFieldValue,
    propertyTypePath: `${buildingPath}.propertyType`,
    residenceUsagePath: `${buildingPath}.residenceUsage`
  });

  // useClearInvalidRiskOptions above only validates against the per-risk-type base options; it does
  // not know about the policy-type mask. When the operator switches policy type, a residence usage
  // that was valid for the old product may not be offered by the new mask (e.g. Tenant Occupied 'R'
  // after switching Rental → Home). Clear it so the operator re-picks a valid option instead of
  // keeping a selection the dropdown no longer offers. Keyed on the mask inputs, not the options
  // array (which is a fresh reference each render).
  useEffect(() => {
    if (
      currentResidenceUsage &&
      residenceUsageOptions.length > 0 &&
      !residenceUsageOptions.some((option) => option.id === currentResidenceUsage)
    ) {
      setFieldValue(`${buildingPath}.residenceUsage`, undefined);
    }
  }, [values.policyType, residenceMaskState, farmRisk?.type, currentResidenceUsage]);

  const isSelectedHeatingSourceUnitCountBased = heatingSourceOptions.find(
    (option) => option.id === farmRisk?.building?.primaryHeatingSource
  )?.unitCountBased;

  // Check if structure category is Barndominium, Mobile Home, or
  // Modular Home. Mobile/modular detection accepts both `propertyType`
  // and the legacy `typeOfHome` field via the shared helper, matching
  // the dec mapper's `isMobileOrModularRC`. (#30735 audit alignment.)
  const isBarndominium = isBarndominiumBuilding(farmRisk?.building);
  const isMobileHome = isMobileHomeBuilding(farmRisk?.building);
  const isModularHome = isModularHomeBuilding(farmRisk?.building);
  const showConstructionType = !getConstructionTypeExemptPropertyTypes(values.state, FarmRiskType.Fo3).includes(
    farmRisk?.building?.propertyType
  );

  return (
    <Grid container spacing={2}>
      <Field
        key={`propertyType-${farmRisk?.type}`}
        xs={6}
        type="select"
        id="farmRisk.building.propertyType"
        name={`${buildingPath}.propertyType`}
        mode="light"
        label="Structure Category*"
        options={getPropertyTypeOptions(farmRisk?.type)}
      />
      <Field
        key={`residenceUsage-${farmRisk?.type}`}
        // fast={false}: options depend on values.policyType (the mask); a FastField would not
        // re-render when the operator switches policy type, leaving the occupancy list stale.
        fast={false}
        xs={6}
        type="select"
        id="farmRisk.building.residenceUsage"
        name={`${buildingPath}.residenceUsage`}
        mode="light"
        label="Residence Type/Usage*"
        options={residenceUsageOptions}
      />

      {needMoreData && !isBarndominium && !isMobileHome && !isModularHome && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}

      {isBarndominium && (
        <BarndomininumDwellingForm
          basePath={farmRiskPath}
          isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
          // Same location-first state the standalone Exterior Wall Finish field uses, so a
          // barndominium's brand-aware options can't diverge from it (devlo review, #34910).
          state={residenceMaskState}
          setFieldValue={setFieldValue}
        />
      )}

      {isMobileHome && (
        <MobileHomeForm
          basePath={farmRiskPath}
          isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
          state={values.state}
          riskType={FarmRiskType.Fo3}
        />
      )}

      {isModularHome && (
        <Field
          xs={6}
          type="select"
          options={lookupsJson.farm.numFamiliesInDwellingOptions}
          id="farmRisk.building.numFamiliesInDwelling"
          name={`${buildingPath}.numFamiliesInDwelling`}
          mode="light"
          label="Families in dwelling*"
        />
      )}

      {showAdditionalFields && (
        <>
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.yearBuilt"
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year Built"
          />
          <Field
            xs={4}
            type="select"
            options={tohOptions}
            id="farmRisk.building.typeOfHome"
            name={`${buildingPath}.typeOfHome`}
            mode="light"
            label="Type of home"
          />
          <Field
            name={`${buildingPath}.ownershipStatus`}
            type="select"
            label="Ownership"
            mode="light"
            options={ownershipStatusOptions}
            xs={4}
          />
          <Field
            xs={3}
            type="select"
            options={nfidOptions}
            id="farmRisk.building.numFamiliesInDwelling"
            name={`${buildingPath}.numFamiliesInDwelling`}
            mode="light"
            label="Families living"
          />
          <Field
            xs={3}
            type="select"
            options={hqOptions}
            id="farmRisk.building.homeQuality"
            name={`${buildingPath}.homeQuality`}
            mode="light"
            label="Home quality"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.sqFt"
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Above-ground sqft"
          />
          {showConstructionType && (
            <Field
              xs={3}
              type="select"
              id="farmRisk.building.constructionType"
              options={ctOptions}
              name={`${buildingPath}.constructionType`}
              mode="light"
              label="Ext. wall construction"
            />
          )}
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numStories"
            name={`${buildingPath}.numStories`}
            mode="light"
            label="Stories"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numHalfBathrooms"
            name={`${buildingPath}.numHalfBathrooms`}
            mode="light"
            label="Half Bathrooms"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numFullBathrooms"
            name={`${buildingPath}.numFullBathrooms`}
            mode="light"
            label="Full Bathrooms"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numFireplaces"
            name={`${buildingPath}.numFireplaces`}
            mode="light"
            label="Fireplaces"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.numWoodBurningStoves"
            name={`${buildingPath}.numWoodBurningStoves`}
            mode="light"
            label="Wood Burning Stoves"
          />
          <Field
            xs={4}
            type="select"
            id="farmRisk.building.basementType"
            options={btOptions}
            name={`${buildingPath}.basementType`}
            mode="light"
            label="Basement/Foundation type"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.basementSqFt"
            name={`${buildingPath}.basementSqFt`}
            mode="light"
            label="Basement finished sqft"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.exteriorWallType"
            options={ewtOptions}
            name={`${buildingPath}.exteriorWallType`}
            mode="light"
            label="Exterior wall finish"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.garageType"
            options={gtOptions}
            name={`${buildingPath}.garageType`}
            mode="light"
            label="Type of Garage"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.garageCarCapacity"
            name={`${buildingPath}.garageCarCapacity`}
            mode="light"
            label="Garage's Car space"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.primaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary Heating"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.secondaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.secondaryHeatingSource`}
            mode="light"
            label="Secondary Heating"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.residenceUsage"
            options={getMaskedResidenceUsageOptions(farmRisk?.type, values.policyType, residenceMaskState)}
            name={`${buildingPath}.residenceUsage`}
            mode="light"
            label="Residence Type"
          />
          <RenovationFieldsForm basePath={farmRiskPath} checkboxXs={6} />

          <Field
            type="select"
            id="farmRisk.building.roofOriginal"
            name={`${buildingPath}.roofOriginal`}
            options={roOptions}
            mode="light"
            label="Original Roof?"
            xs={3}
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.roofYear"
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof Year"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofType"
            options={rtOptions}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofShape"
            options={rsOptions}
            name={`${buildingPath}.roofShape`}
            mode="light"
            label="Roof shape"
          />
          {showConstructionType && (
            <Field
              xs={4}
              type="select"
              id="farmRisk.building.constructionType"
              name={`${buildingPath}.constructionType`}
              mode="light"
              label="Primary construction type"
              options={ctOptions}
            />
          )}
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.latitude"
            name={`${buildingPath}.latitude`}
            mode="light"
            label="Latitude"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.longitude"
            name={`${buildingPath}.longitude`}
            mode="light"
            label="Longitude"
          />
        </>
      )}

      {/* Risk address automatically inherits from location */}
    </Grid>
  );
};

FO3RiskDetail.propTypes = {
  missingDataCodes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

export default FO3RiskDetail;
